import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

import { useAuthProviders } from './providers';

/**
 * Cloudflare Turnstile, the human check on sign-up and sign-in.
 *
 * ## What it is
 *
 * A widget from Cloudflare that decides, mostly invisibly, whether a browser
 * is somebody's, and hands the page a token that says so. The token goes
 * up with the form as `turnstile`, and the server asks Cloudflare whether it
 * is real (see the backend's `Turnstile`). Nothing here is trusted; the
 * widget only produces the token, the server judges it.
 *
 * ## On and off
 *
 * The site key comes from `/auth/providers`, next to the Discord flag, for
 * the same reason: it is set where the secret is. A deployment without it
 * renders nothing and `useTurnstile` reports `ready` at once, so the form
 * works exactly as it did before.
 *
 * ## Tokens are single use
 *
 * A token spent on a refused sign-in (wrong password) is spent. `reset()`
 * asks the widget for another, and the forms call it after any failure.
 */
declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          action?: string;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'compact' | 'flexible';
          appearance?: 'always' | 'execute' | 'interaction-only';
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';

let loading: Promise<void> | null = null;

/** The script, once. Resolves when `window.turnstile` is there. */
function load(): Promise<void> {
  if (window.turnstile) return Promise.resolve();

  loading ??= new Promise<void>((resolve) => {
    window.onTurnstileLoad = () => resolve();

    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  });

  return loading;
}

export interface TurnstileState {
  /** True when the check is off, or when a token is in hand. */
  ready: boolean;
  /** What to send as `turnstile`, or undefined when the check is off. */
  token: string | undefined;
  /** Whether a widget is drawn at all. */
  enabled: boolean;
  /** Ask the widget for a new token: after any failed submit. */
  reset: () => void;
  /** Where the widget goes: `<div ref={box} />` inside the form, when `enabled`. */
  box: RefObject<HTMLDivElement | null>;
}

export function useTurnstile(action: 'login' | 'register', theme: 'light' | 'dark' | 'auto' = 'auto'): TurnstileState {
  const { data } = useAuthProviders();
  const siteKey = data?.turnstile ?? null;
  const [token, setToken] = useState<string | null>(null);
  const box = useRef<HTMLDivElement | null>(null);
  const widget = useRef<string | null>(null);

  useEffect(() => {
    if (siteKey === null) return;

    let gone = false;

    void load().then(() => {
      if (gone || !box.current || !window.turnstile || widget.current !== null) return;

      widget.current = window.turnstile.render(box.current, {
        sitekey: siteKey,
        action,
        theme,
        size: 'flexible',
        callback: (fresh) => setToken(fresh),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null),
      });
    });

    return () => {
      gone = true;

      if (widget.current !== null && window.turnstile) {
        window.turnstile.remove(widget.current);
        widget.current = null;
      }
    };
  }, [siteKey, action, theme]);

  const reset = useCallback(() => {
    setToken(null);

    if (widget.current !== null && window.turnstile) window.turnstile.reset(widget.current);
  }, []);

  const enabled = siteKey !== null;

  return {
    enabled,
    ready: !enabled || token !== null,
    token: enabled ? (token ?? undefined) : undefined,
    reset,
    box,
  };
}

/** The widget's place in a form. Nothing when the check is off. */
export function TurnstileBox({ state }: { state: TurnstileState }) {
  if (!state.enabled) return null;

  return <div ref={state.box} data-turnstile={state.ready ? 'ready' : 'waiting'} className="min-h-[65px]" />;
}
